import { Fragment, FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faAnglesLeft,
	faAnglesRight,
	faChevronDown,
	faChevronRight,
	faArrowUpRight,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CampaignStatusPill } from "../../List/components/CampaignOverview/components/CampaignStatusPill";
import {
	OVERVIEW_COLUMN_GROUPS,
	OverviewColumn,
	OverviewColumnGroupKey,
	visibleColumns,
} from "../config/overviewColumns";
import { OverviewCampaignRow, OverviewListRow, OverviewMetrics } from "../schema/overview";

import { ChannelsCell, LastActivityCell, MetricCell, SendersCell } from "./OverviewCells";

export interface OverviewTableProps {
	rows: OverviewListRow[];
	/** Opens the Campaign Deep Dive for one campaign. */
	onCampaignClick: (row: OverviewListRow, campaign: OverviewCampaignRow) => void;
	/** Pause / resume from the status pill. Omitted while a toggle is in flight. */
	onCampaignToggle?: (row: OverviewListRow, campaign: OverviewCampaignRow) => void;
	togglingCampaignId?: string | null;
}

const CELL = "px-3 py-2 text-right whitespace-nowrap border-b border-border-light";
const COLLAPSED_CELL =
	"px-3 py-2 text-center whitespace-nowrap border-b border-l border-border-light";
const LEAD_CELL =
	"sticky left-0 z-10 px-3 py-2 text-left border-b border-border-light bg-brand-white";

/** Meta columns render bespoke content; everything else goes through MetricCell. */
const renderCell = (
	column: OverviewColumn,
	metrics: OverviewMetrics,
	meta: Pick<OverviewListRow, "channels" | "senders" | "lastActivity">
) => {
	if (column.kind === "collapsed") {
		return <span className="text-text-disabled">—</span>;
	}

	if (column.key === "channels") {
		return <ChannelsCell channels={meta.channels} />;
	}

	if (column.key === "senders") {
		return <SendersCell senders={meta.senders} />;
	}

	if (column.key === "lastActivity") {
		return <LastActivityCell activity={meta.lastActivity} />;
	}

	return <MetricCell column={column} metrics={metrics} />;
};

/**
 * The List & Campaign Overview table: a row per list, expandable into its campaigns.
 *
 * Hand-rolled rather than built on the shared `Table` because of the two-row grouped
 * header — with ~18 metric columns the groups have to be collapsible, and the first
 * column has to stay frozen while the rest scrolls.
 */
export const OverviewTable: FunctionComponent<OverviewTableProps> = ({
	rows,
	onCampaignClick,
	onCampaignToggle,
	togglingCampaignId,
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
	const [collapsedGroups, setCollapsedGroups] = useState<Set<OverviewColumnGroupKey>>(
		() => new Set()
	);

	const toggleRow = useCallback((listId: number) => {
		setExpanded((prev) => {
			const next = new Set(prev);

			if (next.has(listId)) {
				next.delete(listId);
			} else {
				next.add(listId);
			}

			return next;
		});
	}, []);

	const toggleGroup = useCallback((key: OverviewColumnGroupKey) => {
		setCollapsedGroups((prev) => {
			const next = new Set(prev);

			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}

			return next;
		});
	}, []);

	const columns = visibleColumns(collapsedGroups);

	return (
		<div className="w-full overflow-x-auto rounded-xl border border-border-light bg-brand-white">
			<table className="w-full border-separate border-spacing-0 text-sm">
				<thead>
					<tr>
						<th
							className="sticky left-0 z-20 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-meta bg-background-light border-b border-border-light"
							rowSpan={2}
						>
							{t("analytics.overview.columns.listCampaign")}
						</th>
						{OVERVIEW_COLUMN_GROUPS.map((group) => {
							const isCollapsed = collapsedGroups.has(group.key);
							const hint = t(
								isCollapsed ? "analytics.overview.expandGroup" : "analytics.overview.collapseGroup"
							);

							return (
								<th
									key={group.key}
									className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-text-meta bg-background-light border-b border-l border-border-light cursor-pointer hover:text-brand-light"
									colSpan={isCollapsed ? 1 : group.columns.length}
									data-testid={`overview-group-${group.key}`}
									title={hint}
									onClick={() => toggleGroup(group.key)}
								>
									{t(group.labelKey)}
									{/*
									 * Double chevrons pointing the way the columns will move. A single
									 * down-chevron read as a dropdown affordance (PREC-3835) — nothing here
									 * opens a menu.
									 */}
									<Tooltip content={hint}>
										<Icon
											className="ml-1.5 w-2.5 h-2.5"
											data-testid={`overview-group-icon-${group.key}`}
											icon={isCollapsed ? faAnglesRight : faAnglesLeft}
										/>
									</Tooltip>
								</th>
							);
						})}
					</tr>
					<tr>
						{OVERVIEW_COLUMN_GROUPS.map((group) =>
							collapsedGroups.has(group.key) ? (
								<th
									key={group.key}
									className="px-3 py-1.5 text-center text-xs text-text-disabled bg-background-light border-b border-l border-border-light"
								>
									…
								</th>
							) : (
								group.columns.map((column, index) => (
									<th
										key={`${group.key}-${column.key}`}
										className={classNames(
											"px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-text-meta bg-background-light border-b border-border-light whitespace-nowrap",
											column.kind === "meta" ? "text-left" : "text-right",
											index === 0 ? "border-l border-border-light" : ""
										)}
									>
										{t(column.labelKey)}
									</th>
								))
							)
						)}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => {
						const isExpanded = expanded.has(row.listId);

						return (
							<Fragment key={row.listId}>
								<tr className="hover:bg-background-light">
									<td className={LEAD_CELL}>
										<button
											className="flex flex-row items-center gap-2 cursor-pointer text-left font-semibold text-text"
											type="button"
											onClick={() => toggleRow(row.listId)}
										>
											<Icon
												className="w-3 h-3 text-text-meta"
												icon={isExpanded ? faChevronDown : faChevronRight}
											/>
											{row.listName}
											<span className="text-xs font-normal text-text-meta">
												({row.campaigns.length})
											</span>
										</button>
									</td>
									{columns.map((column) => (
										<td
											key={column.key}
											className={column.kind === "collapsed" ? COLLAPSED_CELL : CELL}
										>
											{renderCell(column, row.metrics, row)}
										</td>
									))}
								</tr>

								{isExpanded
									? row.campaigns.map((campaign) => (
											<tr
												key={`${row.listId}-${campaign.campaignId}`}
												className="bg-background-light/60 hover:bg-background-light"
											>
												<td className={classNames(LEAD_CELL, "bg-background-light/60")}>
													<div className="flex flex-row items-center gap-2 pl-5">
														<button
															className="group flex min-w-0 flex-row items-center gap-1.5 cursor-pointer text-left text-text"
															type="button"
															onClick={() => onCampaignClick(row, campaign)}
														>
															<span className="truncate">{campaign.name}</span>
															<Icon
																className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover:opacity-100 text-brand-light"
																icon={faArrowUpRight}
															/>
														</button>
														{/*
														 * Pushed to the right edge of the column so the status pills line up
														 * as one scannable column instead of tracking each campaign's name
														 * length.
														 */}
														<div className="ml-auto shrink-0">
															{campaign.status ? (
																<CampaignStatusPill
																	disabled={togglingCampaignId === campaign.campaignId}
																	status={campaign.status}
																	onToggle={
																		onCampaignToggle
																			? () => onCampaignToggle(row, campaign)
																			: undefined
																	}
																/>
															) : null}
														</div>
													</div>
												</td>
												{columns.map((column) => (
													<td
														key={column.key}
														className={column.kind === "collapsed" ? COLLAPSED_CELL : CELL}
													>
														{renderCell(column, campaign.metrics, campaign)}
													</td>
												))}
											</tr>
										))
									: null}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
